import { addDays, differenceInCalendarDays, format } from 'date-fns';
import { parseLocalDate, formatLocalDate } from '@/utils/date';

/**
 * Build an array of date strings (yyyy-MM-dd) for every day in the inclusive
 * range [startDate, endDate].
 *
 * Used to render the day header and to map a dropped column back to a date.
 */
export function daysBetween(startDate: string, endDate: string): string[] {
  const start = parseLocalDate(startDate);
  const end = parseLocalDate(endDate);
  const totalDays = differenceInCalendarDays(end, start);
  if (totalDays < 0) return [];

  const result: string[] = [];
  for (let i = 0; i <= totalDays; i++) {
    result.push(formatLocalDate(addDays(start, i)));
  }
  return result;
}

/**
 * Index of `date` within a `[startDate, endDate]` window, where the start
 * day itself is column 0. Returns -1 if `date` is outside the window.
 */
export function dayIndex(date: string, startDate: string): number {
  const a = parseLocalDate(startDate);
  const b = parseLocalDate(date);
  const diff = differenceInCalendarDays(b, a);
  return diff >= 0 ? diff : -1;
}

/**
 * Weekend = Saturday or Sunday (matches the vis-timeline weekday header).
 */
export function isWeekend(date: string): boolean {
  const day = parseLocalDate(date).getDay();
  return day === 0 || day === 6;
}

/**
 * True if `date` is today in the browser's local timezone.
 */
export function isToday(date: string): boolean {
  return formatLocalDate(new Date()) === date;
}

/**
 * Two-letter weekday label used by the day header (Su, Mo, Tu, ...).
 */
export function weekdayShortLabel(date: string): string {
  const weekdays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  return weekdays[parseLocalDate(date).getDay()];
}

/**
 * Full month name used by the day header's month strip (e.g. "August").
 * Rendered uppercase via CSS to match the Toggl Plan look.
 */
export function monthLabel(date: string): string {
  return format(parseLocalDate(date), 'LLLL');
}

/**
 * True when the month strip should show a label at this column: on the
 * first visible day (so the user always has month context at the left
 * edge) and on every column where the month changes (i.e. the 1st).
 */
export function shouldShowMonthLabel(date: string, previousDate?: string): boolean {
  if (!previousDate) return true;
  return date.slice(5, 7) !== previousDate.slice(5, 7);
}

/**
 * Format a duration given in minutes as "H:MM" (hours:minutes, zero-padded).
 */
export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return `${hours}:${mins.toString().padStart(2, '0')}`;
}

/**
 * Format a duration for the compact TimelineCard metadata row.
 *
 * Reports and exports intentionally keep using `formatDuration` (`H:MM`),
 * while timeline cards use the more scannable `45m` / `1h` / `1h 30m` form.
 */
export function formatTimelineDuration(minutes: number): string {
  const safeMinutes = Math.max(0, Math.round(minutes));
  const hours = Math.floor(safeMinutes / 60);
  const mins = safeMinutes % 60;

  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

/**
 * Visible end column of a Task, given the stored inclusive `date` and
 * optional inclusive `endDate`. The rendered Timeline treats the end as
 * exclusive (one day after the stored end) so multi-day entries cover the
 * right number of columns. Stored endDate remains inclusive in the DB.
 *
 * For a same-day entry (no endDate), the visible end is `date + 1 day`.
 */
export function visibleEndDate(date: string, endDate?: string | null): string {
  const inclusiveEnd = endDate ?? date;
  return formatLocalDate(addDays(parseLocalDate(inclusiveEnd), 1));
}
